import { useMemo } from "react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
    Box,
    Checkbox,
    Chip,
    Collapse,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import { hasSchema } from "../../../utils/dashboardSchemaDraft";

// Árvore Fabricante → Função → Modelo com seleção tri-state (dedicada, não
// reusa hierarchyNode, que tem CRUD embutido e seleção única).
// Seleção é um Set de ids de modelo; fabricante/função derivam dos filhos visíveis.

// deleteFabricante/removeNode deixam nós placeholder (id null); filtra pra
// não virarem fantasma na árvore
const nosReais = (lista) => (lista || []).filter((no) => no && no.id != null);

function triState(ids, selecionados) {
    if (ids.length === 0) return { marcado: false, indeterminado: false };
    let marcados = 0;
    for (const id of ids) if (selecionados.has(id)) marcados += 1;
    return {
        marcado: marcados === ids.length,
        indeterminado: marcados > 0 && marcados < ids.length,
    };
}

export default function ModeloCheckTree({
    fabricantes,
    selecionados,
    onToggleMuitos,
    expandidos,
    onToggleExpand,
    excluirModeloId,
    styles,
}) {
    const arvore = useMemo(() => {
        return nosReais(fabricantes).map((fab) => {
            const funcoes = nosReais(fab.Funcoes).map((fun) => {
                const modelos = nosReais(fun.Modelos).filter(
                    (mod) => mod.id !== excluirModeloId,
                );
                return { ...fun, modelosVisiveis: modelos };
            });
            return { ...fab, funcoesVisiveis: funcoes };
        });
    }, [fabricantes, excluirModeloId]);

    if (arvore.length === 0) {
        return (
            <Box sx={styles.bulkTreeVazia}>
                <Typography variant="body2" color="text.disabled">
                    Nenhum modelo corresponde à busca.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={styles.bulkTree}>
            {arvore.map((fab) => {
                const idsFab = fab.funcoesVisiveis.flatMap((fun) =>
                    fun.modelosVisiveis.map((mod) => mod.id),
                );
                const estadoFab = triState(idsFab, selecionados);
                const fabAberto = expandidos.includes(String(fab.id));

                return (
                    <Box key={fab.id}>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <IconButton
                                size="small"
                                onClick={() => onToggleExpand(String(fab.id))}
                            >
                                {fabAberto ? (
                                    <ExpandLess fontSize="inherit" />
                                ) : (
                                    <ExpandMore fontSize="inherit" />
                                )}
                            </IconButton>
                            <Checkbox
                                size="small"
                                checked={estadoFab.marcado}
                                indeterminate={estadoFab.indeterminado}
                                disabled={idsFab.length === 0}
                                onChange={() =>
                                    onToggleMuitos(idsFab, !estadoFab.marcado)
                                }
                            />
                            <Typography
                                sx={styles.bulkNivel1}
                                onClick={() => onToggleExpand(String(fab.id))}
                            >
                                {fab.fabricante}
                            </Typography>
                        </Stack>

                        <Collapse in={fabAberto} unmountOnExit>
                            <Box sx={styles.bulkIndent}>
                                {fab.funcoesVisiveis.map((fun) => {
                                    const idsFun = fun.modelosVisiveis.map((mod) => mod.id);
                                    const estadoFun = triState(idsFun, selecionados);
                                    const funAberta = expandidos.includes(String(fun.id));

                                    return (
                                        <Box key={fun.id}>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onToggleExpand(String(fun.id))}
                                                >
                                                    {funAberta ? (
                                                        <ExpandLess fontSize="inherit" />
                                                    ) : (
                                                        <ExpandMore fontSize="inherit" />
                                                    )}
                                                </IconButton>
                                                <Checkbox
                                                    size="small"
                                                    checked={estadoFun.marcado}
                                                    indeterminate={estadoFun.indeterminado}
                                                    disabled={idsFun.length === 0}
                                                    onChange={() =>
                                                        onToggleMuitos(idsFun, !estadoFun.marcado)
                                                    }
                                                />
                                                <Typography
                                                    sx={styles.bulkNivel2}
                                                    onClick={() => onToggleExpand(String(fun.id))}
                                                >
                                                    {fun.funcao || fun.nome}
                                                </Typography>
                                            </Stack>

                                            <Collapse in={funAberta} unmountOnExit>
                                                <Box sx={styles.bulkIndent}>
                                                    {fun.modelosVisiveis.map((mod) => (
                                                        <Stack
                                                            key={mod.id}
                                                            direction="row"
                                                            alignItems="center"
                                                            gap={0.5}
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                checked={selecionados.has(mod.id)}
                                                                onChange={() =>
                                                                    onToggleMuitos(
                                                                        [mod.id],
                                                                        !selecionados.has(mod.id),
                                                                    )
                                                                }
                                                            />
                                                            <Typography sx={styles.bulkNivel3}>
                                                                {mod.modelo}
                                                            </Typography>
                                                            {hasSchema(mod) && (
                                                                <Tooltip title="Este modelo já possui schema">
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        label="tem schema"
                                                                        sx={styles.bulkChipSchema}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
}
